import { Box, Button, CircularProgress, Typography } from '@mui/material';
import * as d3 from 'd3';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { StatuteStatisticsResponse } from '../../types/StatuteStatistics.type';
import { RaceLegend, TotalLegend } from './HeatmapLegend';
import {
  MIN_CELL_WIDTH,
  RACE_COLORS,
  RaceColor,
  TAIL_BIN,
  TimeUnit,
  UNKNOWN_BIN,
  UNKNOWN_YEAR,
  chooseTimeUnit,
  dateToBin,
  detectTailYears,
  formatBinLabel,
  isSpecialBin,
  isUnknownYearMonth,
  yearMonthToBin,
} from './heatmapTimeBins';

const STAGE_ORDER = ['Arrest', 'Charging', 'Filing', 'Conviction', 'Sentencing'];
const RACE_ORDER = ['White', 'Black', 'Asian', 'Latinx'];

const MARGIN = { top: 100, right: 20, bottom: 40, left: 100 };
const DIMMED_OPACITY = 0.3;
const MAX_CELL_WIDTH = 60;

const MIN_YEAR_MONTH = 190001;

interface TooltipData {
  stage: string;
  timeBin: string;
  counts: Record<string, number>;
  total: number;
  x: number;
  y: number;
}

interface GridDatum {
  stage: string;
  timeBin: string;
  race: string;
  count: number;
}

interface DataAvailabilityHeatmapProps {
  statisticsData: StatuteStatisticsResponse | undefined;
  startDate: Date | null;
  endDate: Date | null;
  isLoading?: boolean;
}

const containerSx = {
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  padding: '16px',
  marginX: '16px',
  position: 'relative' as const,
};

function DataAvailabilityHeatmap({ statisticsData, startDate, endDate, isLoading }: DataAvailabilityHeatmapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const [mode, setMode] = useState<'total' | 'by-race'>('total');
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Track container width - callback ref + ResizeObserver
  const containerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    containerRef.current = node;
    if (node) {
      const ro = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        setContainerWidth(width);
      });
      ro.observe(node);
      roRef.current = ro;
    }
  }, []);

  // Detect tail years and unknown date.
  const tailInfo = useMemo(() => {
    if (!statisticsData?.statute_statistics?.length) return null;
    const stats = statisticsData.statute_statistics.filter((s) => s.stage !== null && s.year_month >= MIN_YEAR_MONTH);
    return detectTailYears(stats);
  }, [statisticsData]);

  const hasUnknown = useMemo(() => {
    if (!statisticsData?.statute_statistics?.length) return false;
    return statisticsData.statute_statistics.some((s) => s.stage !== null && isUnknownYearMonth(s.year_month));
  }, [statisticsData]);

  const getBin = useCallback(
    (yearMonth: number, unit: TimeUnit): string => {
      if (isUnknownYearMonth(yearMonth)) return UNKNOWN_BIN;
      const year = Math.floor(yearMonth / 100);
      if (tailInfo?.tailYears.has(year)) return TAIL_BIN;
      return yearMonthToBin(yearMonth, unit);
    },
    [tailInfo],
  );

  // Choose time unit based on main data
  const timeUnit = useMemo<TimeUnit>(() => {
    if (!statisticsData?.statute_statistics?.length || containerWidth === 0) return 'quarter';
    const mainStats = statisticsData.statute_statistics.filter((s) => {
      if (s.stage === null || s.year_month < MIN_YEAR_MONTH) return false;
      if (isUnknownYearMonth(s.year_month)) return false;
      const year = Math.floor(s.year_month / 100);
      return !tailInfo?.tailYears.has(year);
    });
    const specialColumns = (hasUnknown ? 1 : 0) + (tailInfo ? 1 : 0);
    const specialColumnWidth = specialColumns * MIN_CELL_WIDTH;
    const availableWidth = containerWidth - MARGIN.left - MARGIN.right - 32 - specialColumnWidth;
    return chooseTimeUnit(mainStats, availableWidth);
  }, [statisticsData, containerWidth, tailInfo, hasUnknown]);

  const { gridData, timeBins, stages, races } = useMemo(() => {
    const empty = {
      gridData: [] as GridDatum[],
      timeBins: [] as string[],
      stages: [] as string[],
      races: [] as string[],
    };
    if (!statisticsData?.statute_statistics?.length) return empty;

    const stats = statisticsData.statute_statistics.filter((s) => s.stage !== null && s.year_month >= MIN_YEAR_MONTH);
    if (!stats.length) return empty;

    const uniqueStages = [...new Set(stats.map((s) => s.stage!))];
    const orderedStages = uniqueStages.sort((a, b) => {
      const ia = STAGE_ORDER.indexOf(a);
      const ib = STAGE_ORDER.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });

    const uniqueRaces = [...new Set(stats.map((s) => s.race))];
    const orderedRaces = [
      ...RACE_ORDER.filter((r) => uniqueRaces.includes(r)),
      ...uniqueRaces.filter((r) => !RACE_ORDER.includes(r)),
    ];

    const aggregated = new Map<string, number>();
    stats.forEach((stat) => {
      const bin = getBin(stat.year_month, timeUnit);
      const key = `${stat.stage}|${bin}|${stat.race}`;
      aggregated.set(key, (aggregated.get(key) || 0) + stat.cases_count);
    });

    const timeBinSet = new Set<string>();
    stats.forEach((s) => timeBinSet.add(getBin(s.year_month, timeUnit)));
    const normalBins: string[] = [];
    let hasUnknownData = false;
    let hasTailData = false;
    timeBinSet.forEach((b) => {
      if (b === UNKNOWN_BIN) hasUnknownData = true;
      else if (b === TAIL_BIN) hasTailData = true;
      else normalBins.push(b);
    });
    normalBins.sort();
    const sortedTimeBins = [
      ...(hasUnknownData ? [UNKNOWN_BIN] : []),
      ...(hasTailData ? [TAIL_BIN] : []),
      ...normalBins,
    ];

    const grid: GridDatum[] = [];
    for (const stage of orderedStages) {
      for (const tb of sortedTimeBins) {
        for (const race of orderedRaces) {
          grid.push({ stage, timeBin: tb, race, count: aggregated.get(`${stage}|${tb}|${race}`) || 0 });
        }
      }
    }

    return { gridData: grid, timeBins: sortedTimeBins, stages: orderedStages, races: orderedRaces };
  }, [statisticsData, timeUnit, getBin]);

  const colorScales = useMemo(() => {
    if (!gridData.length) return null;

    if (mode === 'total') {
      const totals = d3.rollup(
        gridData,
        (v) => d3.sum(v, (d) => d.count),
        (d) => `${d.stage}|${d.timeBin}`,
      );
      const nonZero = [...totals.values()].filter((v) => v > 0);
      const domain: [number, number] = nonZero.length ? [d3.min(nonZero)!, d3.max(nonZero)!] : [0, 1];

      return {
        mode: 'total' as const,
        total: d3.scaleSequential().domain(domain).interpolator(d3.interpolate('#E7F0FF', '#00254C')),
      };
    }

    const raceScales: Record<string, d3.ScaleSequential<string>> = {};
    for (const race of races) {
      const raceCounts = gridData
        .filter((d) => d.race === race)
        .map((d) => d.count)
        .filter((v) => v > 0);
      const domain: [number, number] = raceCounts.length ? [d3.min(raceCounts)!, d3.max(raceCounts)!] : [0, 1];
      const { h, s, l } = (RACE_COLORS[race] ?? { h: 200, s: 0.5, l: 0.5 }) as RaceColor;

      raceScales[race] = d3
        .scaleSequential()
        .domain(domain)

        .interpolator((t: number) => d3.hsl(h, s * (0.5 + t * 0.55), l + (0.93 - l) * (1 - t)).formatHex());
    }

    return { mode: 'by-race' as const, race: raceScales };
  }, [gridData, mode, races]);

  const selectedRange = useMemo(() => {
    if (!startDate || !endDate) return null;
    return { start: dateToBin(startDate, timeUnit), end: dateToBin(endDate, timeUnit) };
  }, [startDate, endDate, timeUnit]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !gridData.length || !colorScales || containerWidth === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const availableWidth = containerWidth;
    const SPECIAL_COLUMN_GAP = 18;
    const hasSpecialColumn = timeBins.some(isSpecialBin);
    const hasMain = timeBins.some((tb) => !isSpecialBin(tb));
    const gap = hasSpecialColumn && hasMain ? SPECIAL_COLUMN_GAP : 0;
    const cellWidth = Math.max(
      MIN_CELL_WIDTH,
      Math.min(MAX_CELL_WIDTH, (availableWidth - MARGIN.left - MARGIN.right - gap) / timeBins.length),
    );
    const cellHeight = 64;
    const width = MARGIN.left + timeBins.length * cellWidth + gap + MARGIN.right;
    const height = MARGIN.top + stages.length * cellHeight + MARGIN.bottom;

    svg.attr('width', width).attr('height', height).attr('viewBox', `0 0 ${width} ${height}`);

    // No Data pattern
    const defs = svg.append('defs');
    const pattern = defs
      .append('pattern')
      .attr('id', 'zeroHatch')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 6)
      .attr('height', 6)
      .attr('patternTransform', 'rotate(45)');
    pattern.append('rect').attr('width', 6).attr('height', 6).attr('fill', '#F4F5F7');
    pattern
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 6)
      .attr('stroke', '#D3D8E1')
      .attr('stroke-width', 1.5);

    // Create a gap between special and main section
    const baseScale = d3
      .scaleBand()
      .domain(timeBins)
      .range([MARGIN.left, MARGIN.left + timeBins.length * cellWidth])
      .padding(0.08);
    const xPos = (tb: string) => baseScale(tb)! + (isSpecialBin(tb) ? 0 : gap);
    const bandwidth = baseScale.bandwidth();
    const yScale = d3
      .scaleBand()
      .domain(stages)
      .range([MARGIN.top, height - MARGIN.bottom])
      .padding(0.12);

    // Date range selection logic
    const binYearRange = (tb: string): { start: number; end: number } | null => {
      if (tb === UNKNOWN_BIN) return { start: UNKNOWN_YEAR, end: UNKNOWN_YEAR };
      if (tb === TAIL_BIN && tailInfo) return { start: tailInfo.tailStart, end: tailInfo.tailEnd };
      const year = parseInt(tb.split('-')[0], 10);
      return Number.isNaN(year) ? null : { start: year, end: year };
    };

    const isInRange = (tb: string) => {
      if (!selectedRange || !startDate || !endDate) return true;
      const range = binYearRange(tb);
      if (!range) return true;
      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();
      return range.start <= endYear && range.end >= startYear;
    };

    // X Axis labels
    const mainBinCount = timeBins.filter((tb) => !isSpecialBin(tb)).length;
    const tickInterval = mainBinCount > 36 ? 3 : mainBinCount > 18 ? 2 : 1;
    let dataAvailabilityIndex = -1;
    timeBins.forEach((tb) => {
      if (!isSpecialBin(tb)) {
        dataAvailabilityIndex += 1;
        if (dataAvailabilityIndex % tickInterval !== 0) return;
      }
      const cx = xPos(tb) + bandwidth / 2;
      const cy = MARGIN.top - 10;
      svg
        .append('text')
        .attr('x', cx)
        .attr('y', cy)
        .attr('text-anchor', 'start')
        .attr('dominant-baseline', 'middle')
        .attr('transform', `rotate(-90, ${cx}, ${cy})`)
        .attr('font-size', '10px')
        .attr('font-weight', '500')
        .attr('fill', '#12294C')
        .attr('font-family', 'Inter, system-ui, sans-serif')
        .text(formatBinLabel(tb, timeUnit, tailInfo));
    });

    // Line between special and main section
    const firstNormalBin = timeBins.find((tb) => !isSpecialBin(tb));
    const lastSpecialBin = [...timeBins].reverse().find((tb) => isSpecialBin(tb));
    if (firstNormalBin && lastSpecialBin) {
      const sepX = (xPos(lastSpecialBin) + bandwidth + xPos(firstNormalBin)) / 2;
      svg
        .append('line')
        .attr('x1', sepX)
        .attr('x2', sepX)
        .attr('y1', MARGIN.top - 4)
        .attr('y2', height - MARGIN.bottom + 4)
        .attr('stroke', '#CBD5E0')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3');
    }

    // Y Axis labels
    stages.forEach((s) => {
      svg
        .append('text')
        .attr('x', MARGIN.left - 12)
        .attr('y', yScale(s)! + yScale.bandwidth() / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '13px')
        .attr('fill', '#12294C')
        .attr('font-weight', '600')
        .attr('font-family', 'Inter, system-ui, sans-serif')
        .text(s);
    });

    // Cells
    const cellsG = svg.append('g');

    if (colorScales.mode === 'total') {
      const totals = d3.rollup(
        gridData,
        (v) => d3.sum(v, (d) => d.count),
        (d) => d.stage,
        (d) => d.timeBin,
      );

      for (const stage of stages) {
        for (const tb of timeBins) {
          const count = totals.get(stage)?.get(tb) || 0;
          const inRange = isInRange(tb);
          cellsG
            .append('rect')
            .attr('x', xPos(tb))
            .attr('y', yScale(stage)!)
            .attr('width', bandwidth)
            .attr('height', yScale.bandwidth())
            .attr('rx', 3)
            .attr('fill', count === 0 ? 'url(#zeroHatch)' : colorScales.total(count))
            .attr('opacity', 0)
            .transition()
            .duration(300)
            .delay(Math.min(stages.indexOf(stage) * 40 + timeBins.indexOf(tb) * 6, 400))
            .attr('opacity', inRange ? 1 : DIMMED_OPACITY);
        }
      }
    } else {
      const lookup = new Map<string, number>();
      gridData.forEach((d) => lookup.set(`${d.stage}|${d.timeBin}|${d.race}`, d.count));

      for (const stage of stages) {
        for (const tb of timeBins) {
          const x = xPos(tb);
          const y = yScale(stage)!;
          const w = bandwidth;
          const h = yScale.bandwidth();
          const miniW = (w - 1) / 2;
          const miniH = (h - 1) / 2;

          const displayRaces = races.slice(0, 4);
          const positions = [
            { race: displayRaces[0], dx: 0, dy: 0 },
            { race: displayRaces[1], dx: miniW + 1, dy: 0 },
            { race: displayRaces[2], dx: 0, dy: miniH + 1 },
            { race: displayRaces[3], dx: miniW + 1, dy: miniH + 1 },
          ].filter((p) => p.race);

          const inRange = isInRange(tb);
          const cellG = cellsG.append('g').attr('transform', `translate(${x}, ${y})`).attr('opacity', 0);

          cellG
            .transition()
            .duration(300)
            .delay(Math.min(stages.indexOf(stage) * 40 + timeBins.indexOf(tb) * 6, 400))
            .attr('opacity', inRange ? 1 : DIMMED_OPACITY);

          positions.forEach(({ race, dx, dy }) => {
            if (!race) return;
            const count = lookup.get(`${stage}|${tb}|${race}`) || 0;
            cellG
              .append('rect')
              .attr('x', dx)
              .attr('y', dy)
              .attr('width', miniW)
              .attr('height', miniH)
              .attr('rx', 2)
              .attr('fill', count === 0 ? 'url(#zeroHatch)' : (colorScales.race[race]?.(count) ?? '#ccc'));
          });
        }
      }
    }

    // Selected date range overlay
    if (selectedRange) {
      const inRangeBins = timeBins.filter(isInRange);
      const startBin = inRangeBins[0];
      const endBin = inRangeBins[inRangeBins.length - 1];

      if (startBin && endBin) {
        const x1 = xPos(startBin) - 3;
        const x2 = xPos(endBin) + bandwidth + 3;
        const overlayG = svg.append('g');

        overlayG
          .append('rect')
          .attr('x', x1)
          .attr('y', MARGIN.top)
          .attr('width', x2 - x1)
          .attr('height', height - MARGIN.top - MARGIN.bottom)
          .attr('fill', 'rgba(43, 161, 228, 0.04)')
          .attr('stroke', '#3A6B90')
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '6,4')
          .attr('rx', 4)
          .attr('opacity', 0)
          .transition()
          .duration(500)
          .delay(300)
          .attr('opacity', 1);

        overlayG
          .append('text')
          .attr('x', (x1 + x2) / 2)
          .attr('y', height - MARGIN.bottom + 18)
          .attr('text-anchor', 'middle')
          .attr('fill', '#3A6B90')
          .attr('font-size', '12px')
          .attr('font-weight', '600')
          .attr('font-family', 'Inter, system-ui, sans-serif')
          .attr('opacity', 0)
          .text('Selected date range')
          .transition()
          .duration(500)
          .delay(300)
          .attr('opacity', 0.8);
      }
    }

    // Interaction layer — each cell cover for tooltip hover
    const interactionG = svg.append('g');
    for (const stage of stages) {
      for (const tb of timeBins) {
        interactionG
          .append('rect')
          .attr('x', xPos(tb))
          .attr('y', yScale(stage)!)
          .attr('width', bandwidth)
          .attr('height', yScale.bandwidth())
          .attr('fill', 'transparent')
          .attr('data-stage', stage)
          .attr('data-timebin', tb)
          .style('cursor', 'pointer');
      }
    }

    interactionG
      .selectAll('rect')
      .on('mouseenter', function (event: MouseEvent) {
        const el = d3.select(this);
        const stage = el.attr('data-stage');
        const timeBin = el.attr('data-timebin');
        if (!stage || !timeBin) return;

        const counts: Record<string, number> = {};
        let total = 0;
        races.forEach((race) => {
          const d = gridData.find((g) => g.stage === stage && g.timeBin === timeBin && g.race === race);
          counts[race] = d?.count ?? 0;
          total += counts[race];
        });

        const containerRect = containerRef.current!.getBoundingClientRect();

        setTooltip({
          stage,
          timeBin,
          counts,
          total,
          x: event.clientX - containerRect.left,
          y: event.clientY - containerRect.top,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });
  }, [
    gridData,
    timeBins,
    stages,
    races,
    mode,
    colorScales,
    selectedRange,
    containerWidth,
    timeUnit,
    tailInfo,
    startDate,
    endDate,
  ]);

  // Tooltip positioning
  const [tooltipPos, setTooltipPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!tooltip || !containerRef.current || !tooltipRef.current) {
      setTooltipPos(null);
      return;
    }

    const container = containerRef.current;
    const ttEl = tooltipRef.current;
    const ttWidth = ttEl.offsetWidth;
    const ttHeight = ttEl.offsetHeight;
    const pad = 12;

    const maxLeft = container.clientWidth - ttWidth;

    // Tooltip centering with cursor
    let left = tooltip.x - ttWidth / 2;
    let top = tooltip.y + pad;

    if (left > maxLeft) left = maxLeft;
    if (left < 0) left = 0;

    // Show above cursor if overflow
    if (top + ttHeight > container.clientHeight) {
      top = tooltip.y - ttHeight - pad;
    }

    setTooltipPos({ left, top });
  }, [tooltip]);

  if (isLoading)
    return (
      <Box
        sx={{
          ...containerSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (!statisticsData?.statute_statistics?.length || !gridData.length)
    return (
      <Box
        sx={{
          ...containerSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
        }}
      >
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
          }}
        >
          No data available for the selected date range.
        </Typography>
      </Box>
    );

  return (
    <Box ref={containerCallbackRef} sx={containerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Data availability</Typography>
        <Box
          sx={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid #CBD5E0', padding: '4px' }}
        >
          <Button
            disableElevation
            onClick={() => setMode('total')}
            variant='contained'
            size='small'
            sx={{
              backgroundColor: mode === 'total' ? 'primary.main' : '#fff',
              color: mode === 'total' ? '#fff' : '#718096',
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            TOTAL
          </Button>
          <Button
            disableElevation
            onClick={() => setMode('by-race')}
            variant='contained'
            size='small'
            sx={{
              backgroundColor: mode === 'by-race' ? 'primary.main' : '#fff',
              color: mode === 'by-race' ? '#fff' : '#718096',
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            BY RACE
          </Button>
        </Box>
      </Box>

      {/* SVG Heatmap */}
      <Box sx={{ overflowX: 'auto' }}>
        <svg ref={svgRef} />
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '20px', flexWrap: 'wrap' }}>
        {mode === 'total' ? (
          <TotalLegend colorScales={colorScales?.mode === 'total' ? colorScales : null} />
        ) : (
          <RaceLegend races={races} />
        )}
      </Box>

      {/* Tooltip */}
      {tooltip && (
        <Box
          ref={tooltipRef}
          sx={{
            position: 'absolute',
            left: tooltipPos?.left ?? tooltip.x,
            top: tooltipPos?.top ?? tooltip.y,
            visibility: tooltipPos ? 'visible' : 'hidden',
            backgroundColor: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            padding: '10px 14px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 10,
            pointerEvents: 'none',
            minWidth: '160px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '6px' }}>
            <Typography variant='caption' sx={{ fontWeight: 600, color: '#2D3748' }}>
              {tooltip.stage}
            </Typography>
            <Typography variant='caption' sx={{ color: '#718096' }}>
              {formatBinLabel(tooltip.timeBin, timeUnit, tailInfo)}
            </Typography>
          </Box>
          {mode === 'total' ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                borderTop: '1px solid #E2E8F0',
                paddingTop: '4px',
                marginTop: '4px',
              }}
            >
              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                Total Cases
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                {tooltip.total.toLocaleString()}
              </Typography>
            </Box>
          ) : (
            <>
              {races.slice(0, 4).map((race) => {
                const c = RACE_COLORS[race] ?? { h: 200, s: 0.5, l: 0.5 };
                return (
                  <Box
                    key={race}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '2px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '2px',
                          backgroundColor: `hsl(${c.h}, ${c.s * 100}%, ${c.l * 100}%)`,
                        }}
                      />
                      <Typography variant='caption' sx={{ color: '#2D3748' }}>
                        {race}
                      </Typography>
                    </Box>
                    <Typography variant='caption' sx={{ fontWeight: 600 }}>
                      {(tooltip.counts[race] ?? 0).toLocaleString()}
                    </Typography>
                  </Box>
                );
              })}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px solid #E2E8F0',
                  paddingTop: '4px',
                  marginTop: '4px',
                }}
              >
                <Typography variant='caption' sx={{ fontWeight: 600 }}>
                  Total Cases
                </Typography>
                <Typography variant='caption' sx={{ fontWeight: 600 }}>
                  {tooltip.total.toLocaleString()}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default DataAvailabilityHeatmap;
